import PaginatedTable from "Components/shared/PaginatedTable";
import { useLocation, useNavigate } from "react-router-dom";
import { useRTL } from "hooks/isRTL";
import DriverSearch from "Components/DriverManagement/DriverSearch";
import { formatCityName } from "utils/formatCityName";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { getDrivers, changeDriverType } from "services/driverService";
import { RegistrationStatusEnum, DriverTypeEnum } from "enums/shared";
import { fallbackValue } from "constants/fallbackConstant";
import {
  descendingAgeSortAtom,
  descendingCreationDateSortAtom,
  descendingModificationDateSortAtom,
  descendingNameSortAtom,
  driverFiltersAtom,
  driverPageAtom,
  driverRowsPerPageAtom,
} from "./atom/driverTableAtoms";
import { useAtom } from "jotai";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { Switch, Snackbar, Alert } from "@mui/material";

const DriverTable = () => {
  const { t } = useRTL();
  const navigate = useNavigate();
  const location = useLocation();

  const [filters, setFilters] = useAtom(driverFiltersAtom);
  const [page, setPage] = useAtom(driverPageAtom);
  const [rowsPerPage, setRowsPerPage] = useAtom(driverRowsPerPageAtom);
  const [descendingNameSort, setDescendingNameSort] = useAtom(descendingNameSortAtom);
  const [descendingAgeSort, setDescendingAgeSort] = useAtom(descendingAgeSortAtom);
  const [descendingCreationDateSort, setDescendingCreationDateSort] =
    useAtom(descendingCreationDateSortAtom);
  const [descendingModificationDateSort, setDescendingModificationDateSort] =
    useAtom(descendingModificationDateSortAtom);

  useEffect(() => {
    const stateStatus = location.state?.status;
    if (stateStatus && Array.isArray(stateStatus) && stateStatus.length > 0) {
      setFilters((prev) => ({
        ...prev,
        status: stateStatus,
      }));
      window.history.replaceState({}, document.title);
    }
  }, []);

  // ============================================
  // FETCH DRIVERS
  // ============================================
  const fetchDrivers = async () => {
    return getDrivers(
      rowsPerPage,
      page + 1,
      filters.fullName,
      filters.phoneNumber,
      filters.city,
      filters.status,
      descendingNameSort,
      descendingAgeSort,
      filters.creationDate,
      descendingCreationDateSort,
      descendingModificationDateSort,
      filters.driverType
    );
  };

  const { data, isLoading } = useQuery({
    queryKey: [
      "drivers",
      {
        page: page + 1,
        pageSize: rowsPerPage,
        ...filters,
        descendingNameSort,
        descendingAgeSort,
        descendingCreationDateSort,
        descendingModificationDateSort,
      },
    ],
    queryFn: fetchDrivers,
    select: (data) => ({
      rows: data.page.map((driver) => {
        const rawType =
          driver.driverType ??
          driver.DriverType ??
          driver.type ??
          driver.driver_type ??
          driver.driverTypeId ??
          driver.driver_type_id ??
          1;

        // 🔥 Force it to be valid number
        const normalizedType = Number(rawType) === 2 ? 2 : 1;

        return {
          ...driver,
          driverType: normalizedType, // final guaranteed type 1 or 2
          city: driver.city ? formatCityName(driver.city) : fallbackValue,
          age: driver.age || fallbackValue,
          status: t(
            Object.keys(RegistrationStatusEnum).find(
              (key) => RegistrationStatusEnum[key] === driver.status
            ) || "Unknown"
          ),
          phoneNumber: driver.phoneNumber ? driver.phoneNumber.replace("_", "") : fallbackValue,
          creationDate: driver.creationDate || fallbackValue,
          modificationDate: driver.modificationDate || fallbackValue,
        };
      }),
      totalRecords: data.totalRecords,
    }),
    keepPreviousData: true,
  });

  // ============================================
  // MUTATION FOR DRIVER TYPE CHANGE
  // ============================================
  const queryClient = useQueryClient();
  const [changingIds, setChangingIds] = useState([]);
  const [notification, setNotification] = useState({
    open: false,
    message: "",
    type: "success",
  });

  const changeTypeMutation = useMutation(
    ({ id, newType }) => changeDriverType(id, newType),
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["drivers"] });
      },
      onSettled: (_data, _err, variables) => {
        setChangingIds((prev) => prev.filter((x) => x !== variables.id));
      },
    }
  );

  // ============================================
  // SORTING
  // ============================================
  const handleSortChange = (columnId, isDescending) => {
    if (columnId === "fullName") setDescendingNameSort(isDescending);
    else if (columnId === "age") setDescendingAgeSort(isDescending);
    else if (columnId === "creationDate") setDescendingCreationDateSort(isDescending);
    else if (columnId === "modificationDate") setDescendingModificationDateSort(isDescending);
  };

  const handleSearch = (value, columnId) => {
    if (columnId === "fullName") setFilters((prev) => ({ ...prev, fullName: value }));
    else if (columnId === "phoneNumber") setFilters((prev) => ({ ...prev, phoneNumber: value }));
    else if (columnId === "city") setFilters((prev) => ({ ...prev, city: value }));
  };

  const handleNameSearch = (value) => {
    setPage(0);
    setFilters((prev) => ({ ...prev, fullName: value }));
  };

  // ============================================
  // COLUMNS
  // ============================================
  const columns = [
    { id: "fullName", label: t("driverName"), sortable: true },

    { id: "status", label: t("registrationStatus"), sortable: true },

    {
      id: "driverType",
      label: t("isExpedited") || "Is Expedited",

      renderCell: (row) => {
        const isExpedited = Number(row.driverType) === 2;

        return (
          <Switch
            checked={isExpedited} // ON ONLY for expedited (2)

            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}

            onChange={(e) => {
              e.stopPropagation();

              const newType = isExpedited ? 1 : 2; // 🔥 Toggle behavior

              // Show notification
              const message = newType === 2
                ? "Driver changed to Expedited"
                : "Driver changed to Regular";
              setNotification({
                open: true,
                message,
                type: "success",
              });

              setChangingIds((prev) =>
                prev.includes(row.id) ? prev : [...prev, row.id]
              );

              changeTypeMutation.mutate({ id: row.id, newType });
            }}

            // Disable while updating
            disabled={changingIds.includes(row.id)}

            title={isExpedited ? "Expedited (2)" : "Regular (1)"}
          />
        );
      },
    },

    {
      id: "phoneNumber",
      label: t("driverphoneNumber"),
      sortable: true,
      placeholder: t("driverphoneNumberholder"),
    },

    {
      id: "city",
      label: t("location"),
      renderCell: (row) => row.city,
      sortable: true,
    },

    {
      id: "creationDate",
      label: t("creationDate"),
      renderCell: (row) =>
        row.creationDate
          ? dayjs(row.creationDate).format("YYYY-MM-DD / HH:mm")
          : fallbackValue,
      sortable: true,
    },

    {
      id: "modificationDate",
      label: t("lastUpdate"),
      renderCell: (row) =>
        row.modificationDate
          ? dayjs(row.modificationDate).format("YYYY-MM-DD / HH:mm")
          : fallbackValue,
      sortable: true,
    },

    { id: "age", label: t("age"), sortable: true },
  ];

  // ============================================
  // PAGINATION
  // ============================================
  const handleChangePage = (_, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const statusOptions = Object.entries(RegistrationStatusEnum).map(
    ([label, value]) => ({
      value,
      label: t(label),
    })
  );

  const handleRegistrationStatusChange = (statuses) => {
    setFilters((prev) => ({
      ...prev,
      status: statuses,
    }));
  };

  // ============================================
  // RENDER
  // ============================================
  return (
    <>
      <DriverSearch
        filters={filters}
        setFilters={setFilters}
        setDescendingNameSort={setDescendingNameSort}
        setDescendingAgeSort={setDescendingAgeSort}
        setDescendingCreationDateSort={setDescendingCreationDateSort}
        setDescendingModificationDateSort={setDescendingModificationDateSort}
        setPage={setPage}
        searchValue={filters.fullName}
        onSearchChange={handleNameSearch}
      />

      <PaginatedTable
        columns={columns}
        rows={data?.rows || []}
        isLoading={isLoading}
        page={page}
        rowsPerPage={rowsPerPage}
        totalRecords={data?.totalRecords || 0}
        handleChangePage={handleChangePage}
        handleDateChange={(columnId, value) => {
          if (columnId === "creationDate") {
            setFilters((prev) => ({
              ...prev,
              creationDate: value,
            }));
          }
        }}
        handleChangeRowsPerPage={handleChangeRowsPerPage}
        showActions={false}
        onRowClick={(row) => navigate(`/driverManagement/DriverDetails/${row.id}`)}
        onSortChange={handleSortChange}
        onSearch={handleSearch}
        descendingNameSort={descendingNameSort}
        filters={filters}
        statusOptions={statusOptions}
        onRegistrationStatusChange={handleRegistrationStatusChange}
      />

      <Snackbar
        open={notification.open}
        autoHideDuration={3000}
        onClose={() => setNotification((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={() => setNotification((prev) => ({ ...prev, open: false }))}
          severity={notification.type}
          sx={{ width: "100%" }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default DriverTable;
